import { DataTable } from '../layout/data-table.js';

// Minimal accessible SVG bar chart for daily series (no chart library). Semantic tokens only.
// The SVG is summarized with role="img" + aria-label; the exact values are available as a table.

export interface DailyPoint {
  /** YYYY-MM-DD (workspace local day). */
  date: string;
  quantity: number;
}

export interface DailyBarChartProps {
  points: readonly DailyPoint[];
  /** e.g. "Minutos de llamada por día". */
  label: string;
  formatValue: (n: number) => string;
  className?: string;
}

const H = 120;
const BAR = 10;
const GAP = 4;

function shortDay(date: string): string {
  const [, m, d] = date.split('-');
  return `${Number(d)}/${Number(m)}`;
}

export function DailyBarChart({
  points,
  label,
  formatValue,
  className,
}: DailyBarChartProps) {
  const max = Math.max(0, ...points.map((p) => p.quantity));
  const total = points.reduce((a, p) => a + p.quantity, 0);
  const peak = points.find((p) => p.quantity === max && max > 0);
  const width = points.length * (BAR + GAP);
  const summary =
    points.length === 0
      ? `${label}: sin datos`
      : `${label}: total ${formatValue(total)} del ${shortDay(points[0]!.date)} al ${shortDay(points.at(-1)!.date)}${peak ? `; máximo ${formatValue(max)} el ${shortDay(peak.date)}` : '; sin consumo registrado'}.`;
  return (
    <figure className={className}>
      <svg
        role="img"
        aria-label={summary}
        viewBox={`0 0 ${width} ${H + 16}`}
        preserveAspectRatio="none"
        className="h-40 w-full"
      >
        <line
          x1={0}
          x2={width}
          y1={H}
          y2={H}
          className="stroke-border-subtle"
          strokeWidth={1}
        />
        {points.map((p, i) => {
          const h =
            max > 0
              ? Math.max(p.quantity > 0 ? 2 : 0, (p.quantity / max) * (H - 8))
              : 0;
          return (
            <rect
              key={p.date}
              x={i * (BAR + GAP) + GAP / 2}
              y={H - h}
              width={BAR}
              height={h}
              rx={2}
              className="fill-brand-primary"
            >
              <title>{`${shortDay(p.date)}: ${formatValue(p.quantity)}`}</title>
            </rect>
          );
        })}
      </svg>
      <figcaption className="mt-1 flex justify-between text-meta text-text-secondary">
        <span>{points[0] ? shortDay(points[0].date) : ''}</span>
        <span>
          {points.length ? `Total: ${formatValue(total)}` : 'Sin datos'}
        </span>
        <span>{points.at(-1) ? shortDay(points.at(-1)!.date) : ''}</span>
      </figcaption>
      <details className="mt-2 text-meta text-text-secondary">
        <summary className="cursor-pointer rounded-control focus-visible:outline-2 focus-visible:outline-focus">
          Ver datos en tabla
        </summary>
        <DataTable
          className="mt-2"
          caption={label}
          maxHeight="16rem"
          density="compact"
          rows={points}
          getRowId={(p) => p.date}
          columns={[
            { id: 'date', header: 'Día', cell: (p) => p.date },
            {
              id: 'q',
              header: 'Consumo',
              numeric: true,
              cell: (p) => formatValue(p.quantity),
            },
          ]}
        />
      </details>
    </figure>
  );
}
